import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useUpdateOpportunityContactRole } from '@/hooks/use-api'
import type { OpportunityContactRole } from '@/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { explainError } from '@/lib/explain-error'

interface EditOpportunityContactRoleModalProps {
  opportunityId: string
  contactRole: OpportunityContactRole | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const CONTACT_ROLES = [
  'Business User',
  'Decision Maker',
  'Economic Buyer',
  'Economic Decision Maker',
  'Executive Sponsor',
  'Influencer',
  'Other',
  'Technical Buyer',
]

export function EditOpportunityContactRoleModal({
  opportunityId,
  contactRole,
  open,
  onOpenChange,
}: EditOpportunityContactRoleModalProps) {
  const { toast } = useToast()
  const updateMutation = useUpdateOpportunityContactRole()

  const [formData, setFormData] = useState<Partial<OpportunityContactRole>>({
    role: undefined,
    is_primary: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Contact modal state

  // Reset form when contactRole changes or modal opens/closes
  useEffect(() => {
    if (contactRole) {
      setFormData({
        role: contactRole.role,
        is_primary: contactRole.is_primary,
      })
      setErrors({})
    }
  }, [contactRole, open])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!contactRole) return

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateMutation.mutateAsync({
        opportunityId,
        id: contactRole.id,
        data: {
          role: formData.role,
          is_primary: formData.is_primary || false,
        },
      })

      toast({
        title: 'Success',
        description: 'Contact role updated successfully.',
      })

      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update contact role')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  if (!contactRole) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="edit-contact-role-description"
      >
        <DialogHeader>
          <DialogTitle>Edit Contact Role</DialogTitle>
          <DialogDescription id="edit-contact-role-description">
            Update the role for {contactRole.contact_name || 'this contact'}. All fields marked with
            * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Info (Read-only) */}
          <div className="space-y-2 bg-muted p-4 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Contact</Label>
              <div className="text-sm font-semibold mt-1">
                {contactRole.contact_id ? (
                  <Link
                    to={`/contacts/${contactRole.contact_id}`}
                    className="font-semibold text-sm text-primary hover:underline"
                    aria-label={`View contact: ${contactRole.contact_name || 'Unknown Contact'}`}
                  >
                    {contactRole.contact_name || 'Unknown Contact'}
                  </Link>
                ) : (
                  <span>{contactRole.contact_name || 'Unknown Contact'}</span>
                )}
              </div>
            </div>
            {contactRole.contact_title && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Title</Label>
                <p className="text-sm mt-1">{contactRole.contact_title}</p>
              </div>
            )}
            {contactRole.contact_email && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                <p className="text-sm mt-1">{contactRole.contact_email}</p>
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role" className="required">
              Role *
            </Label>
            <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
              <SelectTrigger
                id="role"
                aria-required="true"
                aria-invalid={!!errors.role}
                aria-describedby={errors.role ? 'role-error' : undefined}
              >
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && (
              <p id="role-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.role}
              </p>
            )}
          </div>

          {/* Primary Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_primary"
              checked={formData.is_primary}
              onCheckedChange={(checked) => handleChange('is_primary', checked)}
              aria-label="Set as primary contact for this opportunity"
            />
            <Label
              htmlFor="is_primary"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Primary Contact
            </Label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Updating...' : 'Update Contact Role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
